/**
 * City Agency Landing Pages - Wave 3
 * Adds: Buenos Aires, Lagos, Bangkok, Kuala Lumpur, Athens, Vienna, Prague, Bucharest, Cairo, Riyadh
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards. React, TypeScript, cloud-native.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containers, monitoring, managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA, DevOps integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, design systems.' },
  { name: 'Cybersecurity', description: 'Pen testing, security audits, SOC, compliance (ISO 27001, GDPR, HIPAA).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse systems.' },
];

export const CITY_AGENCY_PAGES_WAVE3: Record<string, CityAgencyData> = {

  'buenos-aires': {
    slug: 'software-development-agency-buenos-aires',
    cityName: 'Buenos Aires',
    countryName: 'Argentina',
    flag: '🇦🇷',
    timezone: 'ART (GMT-3)',
    currency: 'ARS/USD',
    title: {
      en: 'Software Development Agency Buenos Aires | Call IT Dev',
      es: 'Agencia Desarrollo Software Buenos Aires | Call IT Dev',
      fr: 'Agence de Développement Informatique Buenos Aires | Call IT Dev',
      pt: 'Agência Desenvolvimento Software Buenos Aires | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Buenos Aires, Argentina. Custom SaaS, web, mobile, AI. Spanish & English teams, from $20/hr. 55% savings vs BA.',
      es: 'Agencia de desarrollo de software para Buenos Aires. SaaS, web, móvil, IA. Equipos hispanohablantes, desde 20 $/h. 55% ahorro vs BA.',
      fr: 'Agence de développement pour Buenos Aires. SaaS, web, mobile, IA. Équipes hispanophones, dès 20 $/h. 55% d\'économies.',
      pt: 'Agência de desenvolvimento para Buenos Aires. SaaS, web, móvel, IA. Equipes hispanófonas, desde 20 $/h.',
    },
    keywords: ['software development agency Buenos Aires', 'desarrollo software Buenos Aires', 'IT outsourcing Argentina', 'nearshore development Buenos Aires'],
    heroTitle: { en: 'Software Development Agency for Buenos Aires', es: 'Agencia de Desarrollo de Software para Buenos Aires', fr: 'Agence de Développement pour Buenos Aires' },
    heroSubtitle: { en: 'Spanish & English engineers. Americas timezone coverage, 55% savings vs Buenos Aires agencies.', es: 'Ingenieros hispanohablantes. Cobertura horaria Américas, 55% ahorro vs agencias de BA.', fr: 'Ingénieurs hispanophones. Couverture Amériques, 55% d\'économies.' },
    whyTitle: { en: 'Why Buenos Aires Companies Choose Call IT Dev', es: 'Por Qué las Empresas de Buenos Aires Eligen Call IT Dev', fr: 'Pourquoi les Entreprises de Buenos Aires Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Spanish & English Teams', description: 'Bilingual engineers for LATAM and global markets.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at $20-35/hr vs $45-70/hr in Buenos Aires.' },
      { icon: '⏰', title: 'Americas Coverage', description: 'Overlap with US East Coast and LATAM working hours.' },
      { icon: '🎓', title: 'Engineering Excellence', description: '200+ engineers with international certifications.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Enterprise security standards, GDPR compliant.' },
      { icon: '📈', title: 'Scalable Teams', description: 'Scale from 2 to 50+ developers rapidly.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: '¿Sus ingenieros hablan español?', answer: 'Sí, todos nuestros ingenieros asignados a clientes hispanohablantes son nativos o C2. Comunicación, documentación y reuniones en español.' },
      { question: 'How does nearshore compare to Buenos Aires?', answer: 'Senior developers in BA cost $45-70/hr. Our rate is $20-35/hr — 55% savings.' },
      { question: 'Are you compliant with Argentine data protection?', answer: 'Yes. Enterprise security standards, compliant with Argentine PDPA and EU GDPR.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. CVs within 5 business days.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers, scale as needed.' },
      { question: 'Do you support US clients from Argentina?', answer: 'Yes — our teams provide Americas timezone coverage for US and LATAM clients.' },
    ],
    localRate: '$45-70',
    ourRate: '$20-35',
    savings: '55%+',
  },

  lagos: {
    slug: 'software-development-agency-lagos',
    cityName: 'Lagos',
    countryName: 'Nigeria',
    flag: '🇳🇬',
    timezone: 'WAT (GMT+1)',
    currency: 'NGN/USD',
    title: {
      en: 'Software Development Agency Lagos | Call IT Dev',
      fr: 'Agence de Développement Informatique Lagos | Call IT Dev',
      ar: 'وكالة تطوير برمجيات لاغوس | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Lagos, Nigeria. Custom SaaS, web, mobile, AI. English-speaking teams, CET timezone, from $20/hr.',
      fr: 'Agence de développement pour Lagos, Nigeria. SaaS, web, mobile, IA. Équipes anglophones, fuseau CET, dès 20 $/h.',
      ar: 'وكالة تطوير برمجيات لخدمة لاغوس، نيجيريا. SaaS، ويب، موبايل، ذكاء اصطناعي. فرق ناطقة بالإنجليزية.',
    },
    keywords: ['software development agency Lagos', 'IT outsourcing Lagos', 'nearshore development Nigeria', 'web development Lagos'],
    heroTitle: { en: 'Software Development Agency for Lagos', fr: 'Agence de Développement pour Lagos' },
    heroSubtitle: { en: 'English-speaking engineers at competitive rates. Same WAT/CET timezone, Africa\'s tech hub partner.', fr: 'Ingénieurs anglophones à tarifs compétitifs. Même fuseau WAT/CET.' },
    whyTitle: { en: 'Why Lagos Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Lagos Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Fluent Teams', description: 'Native English-speaking developers for seamless communication.' },
      { icon: '⏰', title: 'Same WAT/CET Timezone', description: 'Nigeria and Morocco share GMT+1. Full-day overlap.' },
      { icon: '💰', title: '45% Cost Savings', description: 'Competitive rates with higher quality assurance processes.' },
      { icon: '🌍', title: 'Africa Tech Expertise', description: 'Experience with FinTech, AgriTech, and mobile-first solutions for African markets.' },
      { icon: '🛡️', title: 'NDPR Compliant', description: 'Enterprise security standards, compliant with Nigerian Data Protection Regulation.' },
      { icon: '📈', title: 'Scalable', description: 'Scale from 2 to 40+ engineers rapidly.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you work with Nigerian startups?', answer: 'Yes — FinTech, AgriTech, EdTech, and mobile-first startups across West Africa.' },
      { question: 'Are you NDPR compliant?', answer: 'Yes. Enterprise security standards, fully compliant with Nigeria\'s NDPR.' },
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1 and Nigeria WAT (GMT+1) are identical. Full-day real-time collaboration.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
    ],
    localRate: '$30-50',
    ourRate: '$20-35',
    savings: '45%+',
  },

  bangkok: {
    slug: 'software-development-agency-bangkok',
    cityName: 'Bangkok',
    countryName: 'Thailand',
    flag: '🇹🇭',
    timezone: 'ICT (GMT+7)',
    currency: 'THB/USD',
    title: {
      en: 'Software Development Agency Bangkok | Call IT Dev',
      fr: 'Agence de Développement Informatique Bangkok | Call IT Dev',
      ja: 'ソフトウェア開発エージェンシー バンコク | Call IT Dev',
      zh: '软件开发机构曼谷 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bangkok, Thailand. Custom SaaS, web, mobile, AI. English-speaking teams, from $20/hr. SEA coverage.',
      fr: 'Agence de développement pour Bangkok. SaaS, web, mobile, IA. Équipes anglophones, dès 20 $/h.',
      ja: 'バンコク向けソフトウェア開発エージェンシー。SaaS、ウェブ、モバイル、AI。英語チーム、20$/時間〜。',
      zh: '为曼谷提供软件开发服务。SaaS、网站、移动端、AI。英语团队，20美元/小时起。',
    },
    keywords: ['software development agency Bangkok', 'IT outsourcing Bangkok', 'nearshore development Thailand', 'web development Bangkok'],
    heroTitle: { en: 'Software Development Agency for Bangkok', fr: 'Agence de Développement pour Bangkok', ja: 'バンコク向けソフトウェア開発' },
    heroSubtitle: { en: 'English-speaking engineers with Southeast Asia market expertise. Competitive rates, GDPR compliant.', fr: 'Ingénieurs anglophones experts du marché asiatique.', ja: '英語対応エンジニア、東南アジア市場の専門知識。' },
    whyTitle: { en: 'Why Bangkok Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Bangkok Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Fluent', description: 'All engineers communicate fluently in English.' },
      { icon: '💰', title: '40% Cost Savings', description: 'Competitive rates vs Bangkok\'s growing tech scene.' },
      { icon: '🌏', title: 'SEA Expertise', description: 'Experience with Southeast Asian market requirements.' },
      { icon: '🛡️', title: 'PDPA Compliant', description: 'Enterprise security standards, compliant with Thailand\'s PDPA.' },
      { icon: '📈', title: 'Scalable', description: 'Scale from 2 to 40+ engineers.' },
      { icon: '🚀', title: 'Startup-Friendly', description: 'Experience with Bangkok\'s vibrant startup ecosystem.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone work with Bangkok?', answer: 'Morocco GMT+1, Bangkok GMT+7 — 6h difference. We provide overlap during morning Bangkok / afternoon Morocco hours.' },
      { question: 'Are you PDPA compliant?', answer: 'Yes. Enterprise security standards, compliant with Thailand\'s Personal Data Protection Act.' },
      { question: 'Do you have SEA market experience?', answer: 'Yes — mobile-first, e-commerce, and FinTech for Southeast Asian markets.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: '$35-55',
    ourRate: '$20-35',
    savings: '40%+',
  },

  'kuala-lumpur': {
    slug: 'software-development-agency-kuala-lumpur',
    cityName: 'Kuala Lumpur',
    countryName: 'Malaysia',
    flag: '🇲🇾',
    timezone: 'MYT (GMT+8)',
    currency: 'MYR/USD',
    title: {
      en: 'Software Development Agency Kuala Lumpur | Call IT Dev',
      fr: 'Agence de Développement Informatique Kuala Lumpur | Call IT Dev',
      zh: '软件开发机构吉隆坡 | Call IT Dev',
      ar: 'وكالة تطوير برمجيات كوالالمبور | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Kuala Lumpur, Malaysia. Custom SaaS, web, mobile, AI. English & Malay teams, from $20/hr.',
      fr: 'Agence de développement pour Kuala Lumpur. SaaS, web, mobile, IA. Équipes anglophones, dès 20 $/h.',
      zh: '为吉隆坡提供软件开发服务。SaaS、网站、移动端、AI。英语和马来语团队。',
      ar: 'وكالة تطوير برمجيات لكوالالمبور. SaaS، ويب، موبايل، ذكاء اصطناعي.',
    },
    keywords: ['software development agency Kuala Lumpur', 'IT outsourcing KL', 'nearshore development Malaysia', 'web development Kuala Lumpur'],
    heroTitle: { en: 'Software Development Agency for Kuala Lumpur', fr: 'Agence de Développement pour Kuala Lumpur', zh: '吉隆坡软件开发机构' },
    heroSubtitle: { en: 'English-speaking engineers with ASEAN market expertise. Competitive rates, PDPA compliant.', fr: 'Ingénieurs anglophones experts ASEAN. Tarifs compétitifs.', zh: '英语工程师团队，精通东盟市场。竞争力价格。' },
    whyTitle: { en: 'Why KL Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de KL Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Fluent', description: 'All engineers communicate in English — Malaysia\'s business language.' },
      { icon: '💰', title: '45% Cost Savings', description: 'Competitive rates vs Kuala Lumpur tech market.' },
      { icon: '🌏', title: 'ASEAN Expertise', description: 'Experience with Malaysian and ASEAN market compliance.' },
      { icon: '🕌', title: 'Islamic Finance', description: 'Expertise in Islamic FinTech and Shariah-compliant solutions.' },
      { icon: '🛡️', title: 'PDPA Compliant', description: 'ISO 27001, Malaysian PDPA compliant.' },
      { icon: '📈', title: 'Scale Fast', description: 'From 2 to 50+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have Islamic FinTech experience?', answer: 'Yes. Shariah-compliant fintech, Islamic banking platforms, and halal marketplace solutions.' },
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1, KL GMT+8 — 7h difference. Morning overlap available.' },
      { question: 'Are you PDPA compliant?', answer: 'Yes. ISO 27001, fully compliant with Malaysia\'s PDPA.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team?', answer: 'Start with 2 developers, scale as needed.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: '$35-60',
    ourRate: '$20-35',
    savings: '45%+',
  },

  athens: {
    slug: 'software-development-agency-athens',
    cityName: 'Athens',
    countryName: 'Greece',
    flag: '🇬🇷',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Athens | Call IT Dev',
      fr: 'Agence de Développement Informatique Athènes | Call IT Dev',
      de: 'Softwareentwicklung Agentur Athen | Call IT Dev',
      it: 'Agenzia Sviluppo Software Atene | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Athens, Greece. Custom SaaS, web, mobile, AI. English teams, 1h time diff, from €20/hr. 50% savings.',
      fr: 'Agence de développement pour Athènes. SaaS, web, mobile, IA. 1h de décalage, dès 20 €/h. 50% d\'économies.',
      de: 'Softwareentwicklungsagentur für Athen. SaaS, Web, Mobile, KI. 1h Zeitunterschied, ab 20 €/h. 50% Ersparnis.',
      it: 'Agenzia di sviluppo software per Atene. SaaS, web, mobile, IA. 1h di differenza, da 20 €/h.',
    },
    keywords: ['software development agency Athens', 'IT outsourcing Athens', 'nearshore development Greece', 'web development Athens'],
    heroTitle: { en: 'Software Development Agency for Athens', fr: 'Agence de Développement pour Athènes', de: 'Softwareentwicklung Agentur für Athen' },
    heroSubtitle: { en: '1-hour time difference, English-fluent engineers. 50% savings vs Athens agencies.', fr: '1h de décalage, ingénieurs anglophones. 50% d\'économies.', de: '1h Zeitunterschied, englischsprachige Ingenieure. 50% Ersparnis.' },
    whyTitle: { en: 'Why Athens Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Athènes Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'CET vs EET — only 1 hour apart for real-time collaboration.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Senior devs at €20-35/hr vs €40-65/hr in Athens.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'All communication in English.' },
      { icon: '🏛️', title: 'EU Market Access', description: 'GDPR compliant, EU business experience.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise security standards, EU GDPR compliant.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 40+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Athens?', answer: 'Senior devs in Athens cost €40-65/hr. Our rate is €20-35/hr — 50% savings with 1h time difference.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, fully EU GDPR compliant.' },
      { question: 'Do you work with Greek startups?', answer: 'Yes. Tourism tech, maritime tech, and EU-market startups.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: '€40-65',
    ourRate: '€20-35',
    savings: '50%+',
  },

  vienna: {
    slug: 'software-development-agency-vienna',
    cityName: 'Vienna',
    countryName: 'Austria',
    flag: '🇦🇹',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Vienna | Call IT Dev',
      de: 'Softwareentwicklung Agentur Wien | Call IT Dev',
      fr: 'Agence de Développement Informatique Vienne | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Vienna, Austria. Custom SaaS, web, mobile, AI. German & English teams, CET, from €25/hr. 55% savings.',
      de: 'Softwareentwicklungsagentur für Wien. SaaS, Web, Mobile, KI. Deutsch- und englischsprachige Teams, CET, ab 25 €/h. 55% Ersparnis.',
      fr: 'Agence de développement pour Vienne. SaaS, web, mobile, IA. Équipes germanophones, fuseau CET, dès 25 €/h.',
    },
    keywords: ['software development agency Vienna', 'Softwareentwicklung Wien', 'IT outsourcing Vienna', 'nearshore development Austria'],
    heroTitle: { en: 'Software Development Agency for Vienna', de: 'Softwareentwicklung Agentur für Wien', fr: 'Agence de Développement pour Vienne' },
    heroSubtitle: { en: 'German & English engineers at nearshore rates. Same CET timezone, 55% savings vs Vienna.', de: 'Deutsch- und englischsprachige Ingenieure zu Nearshore-Preisen. Gleiche CET-Zeitzone, 55% Ersparnis.', fr: 'Ingénieurs germanophones à tarifs nearshore. Même fuseau CET.' },
    whyTitle: { en: 'Why Vienna Companies Choose Call IT Dev', de: 'Warum Wiener Unternehmen Call IT Dev Wählen', fr: 'Pourquoi les Entreprises de Vienne Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Identical working hours with Vienna.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €65-100/hr in Vienna.' },
      { icon: '🗣️', title: 'German & English', description: 'Bilingual teams for DACH market projects.' },
      { icon: '🏦', title: 'DACH Expertise', description: 'Experience with Austrian and DACH market compliance.' },
      { icon: '🛡️', title: 'DSG/GDPR Compliant', description: 'Enterprise security standards, Austrian DSG and EU GDPR compliant.' },
      { icon: '✈️', title: '3.5h Direct Flight', description: 'Casablanca-Vienna direct connections.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Sprechen Ihre Ingenieure Deutsch?', answer: 'Ja, wir haben deutschsprachige Ingenieure für DACH-Projekte. Kommunikation, Dokumentation und Meetings auf Deutsch.' },
      { question: 'How does nearshore compare to Vienna?', answer: 'Senior devs in Vienna cost €65-100/hr. Our rate is €25-40/hr — 55% savings, same CET timezone.' },
      { question: 'Are you DSG/GDPR compliant?', answer: 'Yes. Enterprise security standards, compliant with Austrian DSG and EU GDPR.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers, scale to 40+.' },
      { question: 'Do you work with Austrian startups?', answer: 'Yes — SaaS, FinTech, and enterprise companies in the DACH region.' },
    ],
    localRate: '€65-100',
    ourRate: '€25-40',
    savings: '55%+',
  },

  prague: {
    slug: 'software-development-agency-prague',
    cityName: 'Prague',
    countryName: 'Czech Republic',
    flag: '🇨🇿',
    timezone: 'CET (GMT+1)',
    currency: 'CZK/EUR',
    title: {
      en: 'Software Development Agency Prague | Call IT Dev',
      de: 'Softwareentwicklung Agentur Prag | Call IT Dev',
      fr: 'Agence de Développement Informatique Prague | Call IT Dev',
      pl: 'Agencja Rozwoju Oprogramowania Praga | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Prague, Czech Republic. Custom SaaS, web, mobile, AI. CET timezone, from €20/hr. 40% savings vs Prague.',
      de: 'Softwareentwicklungsagentur für Prag. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 20 €/h. 40% Ersparnis.',
      fr: 'Agence de développement pour Prague. SaaS, web, mobile, IA. Fuseau CET, dès 20 €/h. 40% d\'économies.',
      pl: 'Agencja rozwoju oprogramowania dla Pragi. SaaS, web, mobile, AI. Strefa CET, od 20 €/h.',
    },
    keywords: ['software development agency Prague', 'IT outsourcing Prague', 'nearshore development Czech Republic', 'vývoj softwaru Praha'],
    heroTitle: { en: 'Software Development Agency for Prague', de: 'Softwareentwicklung für Prag', fr: 'Agence de Développement pour Prague' },
    heroSubtitle: { en: 'CET-aligned engineers at competitive rates. 40% savings vs Prague agencies.', de: 'CET-Ingenieure zu wettbewerbsfähigen Preisen. 40% Ersparnis.', fr: 'Ingénieurs CET à tarifs compétitifs. 40% d\'économies.' },
    whyTitle: { en: 'Why Prague Companies Choose Call IT Dev', de: 'Warum Prager Unternehmen Call IT Dev Wählen', fr: 'Pourquoi les Entreprises de Prague Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Full overlap with Prague working hours.' },
      { icon: '💰', title: '40% Cost Savings', description: 'Competitive even vs Prague\'s already-efficient market.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'All communication in English.' },
      { icon: '🇪🇺', title: 'EU Compliance', description: 'GDPR compliant, enterprise security standards.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise security standards, EU GDPR compliant.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 40+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Prague?', answer: 'Senior devs in Prague cost €35-55/hr. Our rate is €20-35/hr — 40% savings, same CET timezone.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, fully EU GDPR compliant.' },
      { question: 'Do you work with Czech startups?', answer: 'Yes. SaaS, gaming, and enterprise companies.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP.' },
    ],
    localRate: '€35-55',
    ourRate: '€20-35',
    savings: '40%+',
  },

  bucharest: {
    slug: 'software-development-agency-bucharest',
    cityName: 'Bucharest',
    countryName: 'Romania',
    flag: '🇷🇴',
    timezone: 'EET (GMT+2)',
    currency: 'RON/EUR',
    title: {
      en: 'Software Development Agency Bucharest | Call IT Dev',
      fr: 'Agence de Développement Informatique Bucarest | Call IT Dev',
      de: 'Softwareentwicklung Agentur Bukarest | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bucharest, Romania. Custom SaaS, web, mobile, AI. 1h time diff, from €20/hr. 40% savings vs Bucharest.',
      fr: 'Agence de développement pour Bucarest. SaaS, web, mobile, IA. 1h de décalage, dès 20 €/h. 40% d\'économies.',
      de: 'Softwareentwicklungsagentur für Bukarest. SaaS, Web, Mobile, KI. 1h Zeitunterschied, ab 20 €/h.',
    },
    keywords: ['software development agency Bucharest', 'IT outsourcing Bucharest', 'nearshore development Romania', 'dezvoltare software București'],
    heroTitle: { en: 'Software Development Agency for Bucharest', fr: 'Agence de Développement pour Bucarest' },
    heroSubtitle: { en: '1-hour time difference, competitive rates. 40% savings vs Bucharest agencies.', fr: '1h de décalage, tarifs compétitifs. 40% d\'économies.' },
    whyTitle: { en: 'Why Bucharest Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Bucarest Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'CET vs EET — near-full working day overlap.' },
      { icon: '💰', title: '40% Cost Savings', description: 'Competitive rates vs Romania\'s growing tech market.' },
      { icon: '🗣️', title: 'English & French', description: 'French-speaking engineers available — Romania\'s Francophone tradition.' },
      { icon: '🇪🇺', title: 'EU Compliance', description: 'GDPR compliant, enterprise security standards.' },
      { icon: '🛡️', title: 'ANSPDCP Compliant', description: 'Enterprise security standards, compliant with Romanian data protection authority.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 40+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Bucharest?', answer: 'Senior devs in Bucharest cost €30-50/hr. Our rate is €20-35/hr — 40% savings, 1h time difference.' },
      { question: 'Do your engineers speak French?', answer: 'Yes — French is widely spoken. We offer francophone teams for Romanian and French-market projects.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, EU GDPR compliant.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP.' },
    ],
    localRate: '€30-50',
    ourRate: '€20-35',
    savings: '40%+',
  },

  cairo: {
    slug: 'software-development-agency-cairo',
    cityName: 'Cairo',
    countryName: 'Egypt',
    flag: '🇪🇬',
    timezone: 'EET (GMT+2)',
    currency: 'EGP/USD',
    title: {
      en: 'Software Development Agency Cairo | Call IT Dev',
      ar: 'وكالة تطوير برمجيات القاهرة | Call IT Dev',
      fr: 'Agence de Développement Informatique Le Caire | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Cairo, Egypt. Custom SaaS, web, mobile, AI. Arabic & English teams, from $20/hr. MENA coverage.',
      ar: 'وكالة تطوير برمجيات للقاهرة. SaaS مخصص، ويب، موبايل، ذكاء اصطناعي. فرق عربية وإنجليزية، من 20 $/ساعة.',
      fr: 'Agence de développement pour Le Caire. SaaS, web, mobile, IA. Équipes arabophones, dès 20 $/h.',
    },
    keywords: ['software development agency Cairo', 'IT outsourcing Cairo', 'nearshore development Egypt', 'تطوير برمجيات القاهرة'],
    heroTitle: { en: 'Software Development Agency for Cairo', ar: 'وكالة تطوير برمجيات للقاهرة', fr: 'Agence de Développement pour Le Caire' },
    heroSubtitle: { en: 'Arabic & English engineers. 1h time difference, MENA market expertise.', ar: 'مهندسون عرب وإنجليز. ساعة واحدة فرق، خبرة في سوق الشرق الأوسط.', fr: 'Ingénieurs arabophones et anglophones. 1h de décalage, expertise MENA.' },
    whyTitle: { en: 'Why Cairo Companies Choose Call IT Dev', ar: 'لماذا تختار شركات القاهرة Call IT Dev', fr: 'Pourquoi les Entreprises du Caire Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Arabic & English', description: 'Bilingual teams for MENA and global markets.' },
      { icon: '⏰', title: '1h Time Difference', description: 'CET vs EET — near-identical working hours.' },
      { icon: '💰', title: '35% Cost Savings', description: 'Competitive rates with superior quality assurance.' },
      { icon: '🌍', title: 'MENA Expertise', description: 'Deep experience with MENA market compliance and culture.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Enterprise security standards, Egyptian PDPL compliant.' },
      { icon: '📈', title: 'Scale Fast', description: 'From 2 to 50+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'هل يتحدث مهندسوكم العربية؟', answer: 'نعم، لدينا فرق هندسية ثنائية اللغة عربية-إنجليزية لمشاريع منطقة الشرق الأوسط وشمال أفريقيا.' },
      { question: 'How does the timezone work with Cairo?', answer: 'Morocco GMT+1, Cairo EET (GMT+2) — only 1 hour difference. Full-day overlap.' },
      { question: 'Do you work with Egyptian startups?', answer: 'Yes — FinTech, EdTech, and e-commerce startups across MENA.' },
      { question: 'Are you compliant with Egyptian data protection?', answer: 'Yes. Enterprise security standards, compliant with Egypt\'s Personal Data Protection Law.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
    ],
    localRate: '$25-45',
    ourRate: '$20-35',
    savings: '35%+',
  },

  riyadh: {
    slug: 'software-development-agency-riyadh',
    cityName: 'Riyadh',
    countryName: 'Saudi Arabia',
    flag: '🇸🇦',
    timezone: 'AST (GMT+3)',
    currency: 'SAR/USD',
    title: {
      en: 'Software Development Agency Riyadh | Call IT Dev',
      ar: 'وكالة تطوير برمجيات الرياض | Call IT Dev',
      fr: 'Agence de Développement Informatique Riyad | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Riyadh, Saudi Arabia. Custom SaaS, web, mobile, AI. Arabic & English teams. Vision 2030 aligned.',
      ar: 'وكالة تطوير برمجيات للرياض. SaaS مخصص، ويب، موبايل، ذكاء اصطناعي. متوافق مع رؤية 2030.',
      fr: 'Agence de développement pour Riyad. SaaS, web, mobile, IA. Équipes arabophones, Vision 2030.',
    },
    keywords: ['software development agency Riyadh', 'IT outsourcing Riyadh', 'تطوير برمجيات الرياض', 'nearshore development Saudi Arabia', 'Vision 2030 tech'],
    heroTitle: { en: 'Software Development Agency for Riyadh', ar: 'وكالة تطوير برمجيات للرياض', fr: 'Agence de Développement pour Riyad' },
    heroSubtitle: { en: 'Arabic & English engineers. Vision 2030 aligned, 50% savings vs Riyadh rates.', ar: 'مهندسون عرب وإنجليز. متوافق مع رؤية 2030، توفير 50% مقارنة بأسعار الرياض.', fr: 'Ingénieurs arabophones. Vision 2030, 50% d\'économies vs Riyad.' },
    whyTitle: { en: 'Why Riyadh Companies Choose Call IT Dev', ar: 'لماذا تختار شركات الرياض Call IT Dev', fr: 'Pourquoi les Entreprises de Riyad Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Arabic & English', description: 'Bilingual teams for Saudi and international markets.' },
      { icon: '⏰', title: '2h Time Difference', description: 'Morocco GMT+1, Riyadh GMT+3. 6+ hours daily overlap.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Senior devs at $25-40/hr vs $60-100/hr in Riyadh.' },
      { icon: '🏢', title: 'Vision 2030 Ready', description: 'Experience with Saudi digital transformation initiatives.' },
      { icon: '🛡️', title: 'PDPL Compliant', description: 'Enterprise security standards, Saudi PDPL compliant.' },
      { icon: '🚀', title: 'NEOM & Giga Projects', description: 'Tech solutions aligned with Saudi mega-projects.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'هل أنتم متوافقون مع رؤية 2030؟', answer: 'نعم، لدينا خبرة في مشاريع التحول الرقمي المتوافقة مع رؤية المملكة 2030.' },
      { question: 'Do your engineers speak Arabic?', answer: 'Yes. Bilingual Arabic-English engineering teams for MENA market projects.' },
      { question: 'Are you compliant with Saudi PDPL?', answer: 'Yes. Enterprise security standards, fully compliant with Saudi Personal Data Protection Law.' },
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1, Riyadh GMT+3 — 2h difference. 6+ hours of daily overlap.' },
      { question: 'Can you work on government projects?', answer: 'Yes. Experience with government digital transformation and Vision 2030 initiatives.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. CVs within 5 business days.' },
    ],
    localRate: '$60-100',
    ourRate: '$25-40',
    savings: '50%+',
  },
};
